import {formatText} from 'presentation/utils';
import {useGoToRebooking} from 'presentation/hooks';
import {BookingOriginEnum} from 'presentation/modules/booking/shared/constants/bookingOriginEnum';
import {BookingTypeEnum} from 'presentation/modules/booking/shared/constants/bookingTypeEnum';
import {FlowEnum} from 'presentation/modules/booking/shared/constants/flowEnum';
import {useSpecialtyFilter} from './useSpecialtyFilter';
import {useProfessionalScheduling} from './useProfessionalScheduling';
import {useFavoriteDoctorsData} from './useFavoriteDoctorsData';
import {useFavoriteDoctorsNavigation} from './useFavoriteDoctorsNavigation';

const useFavorites = () => {
	const {doctors, isLoading, isError, refetch, bookingTheme} = useFavoriteDoctorsData();
	const {handleGoToHome, setActiveBookingFlowStep} = useFavoriteDoctorsNavigation();

	const {
		specialtySelected,
		subSpecialtySelected,
		handleSpecialtyClick,
		handleResetSpecialty,
		query,
	} = useSpecialtyFilter();

	const {
		handleScheduleAppointment,
		getSelectedAddress,
		checkBookingModal,
		setCheckBookingModal,
		loadingText,
		resetBookingDataSpecialties,
		handleChooseScriptSuggestion,
		scheduledBooking,
		selectedProfessional,
		handleResetProfessionalSelection,
		openUnaccreditedProvidersDrawer,
		handleOpenUnaccreditedProvidersDrawer,
		handleCloseUnaccreditedProvidersDrawer,
	} = useProfessionalScheduling(doctors);

	const {goToRebooking} = useGoToRebooking({
		bookingNumber: scheduledBooking?.bookingNumber || '',
		bookingType: BookingTypeEnum.HEALTH,
		flowType: FlowEnum.HEALTH,
		specialtyCode: scheduledBooking?.specialtyCode ?? '',
		cityCode: getSelectedAddress()?.cityName?.toString(),
		ufCode: getSelectedAddress()?.ufCode?.toString(),
		ufName: getSelectedAddress()?.ufName?.toString(),
		email: '',
		phone: '',
		isTelehealth: scheduledBooking?.bookingType === BookingTypeEnum.TELEHEALTH,
		locationType: 'default',
		specialtyName: scheduledBooking?.specialtyName ?? '',
		subSpecialtyCode: scheduledBooking?.subSpecialtyCode ?? '',
		setIsBornWellActiveState: () => {},
		getBookingTypeAndOrigin: () => BookingOriginEnum.BOOKING_HEALTH,
		currentAppointmentLocation: {
			city: getSelectedAddress()?.cityName?.toString() || '',
			state: {
				cdUf: getSelectedAddress()?.ufCode?.toString() || '',
			},
		},
		setActiveBookingFlowStep,
		isFavoriteDoctorsFlow: true,
		professionalCode: selectedProfessional
			? selectedProfessional.cdPrestadorFisico.toString()
			: '',
		professionalName: selectedProfessional ? selectedProfessional.nmPrestadorFisico : '',
	});

	const formatSpecialtyTitle = ({
		specialtyName,
		quantity,
		subSpecialtyName,
	}: {
		specialtyName: string;
		quantity: number;
		subSpecialtyName?: string;
	}) => {
		const formattedSpecialtyName = subSpecialtyName
			? `${formatText(specialtyName)} - ${formatText(subSpecialtyName)}`
			: formatText(specialtyName);
		return `${formattedSpecialtyName} (${quantity})`;
	};

	return {
		isLoading,
		isError,
		refetch,
		specialtySelected,
		subSpecialtySelected,
		formatSpecialtyTitle,
		handleGoToHome,
		handleSpecialtyClick,
		handleResetSpecialty,
		doctors,
		query,
		handleScheduleAppointment,
		bookingTheme,
		swapSpecialtyCallback: goToRebooking,
		checkBookingModal,
		setCheckBookingModal,
		loadingText,
		resetBookingDataSpecialties,
		handleChooseScriptSuggestion,
		scheduledBooking,
		selectedProfessional,
		handleResetProfessionalSelection,
		openUnaccreditedProvidersDrawer,
		handleOpenUnaccreditedProvidersDrawer,
		handleCloseUnaccreditedProvidersDrawer,
	};
};

export {useFavorites};
